import type { Locale } from "@/lang";
import { FORMAT_TAGS } from "./format-tags";

/**
 * Readable names of the topic tags used in article front matter (`tags:`). Slugs stay
 * French in the content on purpose (authoring convention, shared by both languages); the
 * visitor only ever sees these labels.
 */
const TOPIC_LABELS: Record<string, { fr: string; en: string }> = {
  agenda: { fr: "Agenda", en: "Calendar" },
  alternance: { fr: "Alternance", en: "Work-study" },
  asana: { fr: "Asana", en: "Asana" },
  "assistant-ia": { fr: "Assistant IA", en: "AI assistant" },
  associations: { fr: "Associations", en: "Nonprofits" },
  "charge-mentale": { fr: "Charge mentale", en: "Mental load" },
  clickup: { fr: "ClickUp", en: "ClickUp" },
  comparatif: { fr: "Comparatif", en: "Comparison" },
  concentration: { fr: "Concentration", en: "Focus" },
  coulisses: { fr: "Coulisses", en: "Behind the scenes" },
  documents: { fr: "Documents", en: "Documents" },
  enseignement: { fr: "Enseignement", en: "Teaching" },
  equipe: { fr: "Équipe", en: "Team" },
  etudiants: { fr: "Étudiants", en: "Students" },
  famille: { fr: "Famille", en: "Family" },
  freelance: { fr: "Freelance", en: "Freelance" },
  "google-tasks": { fr: "Google Tasks", en: "Google Tasks" },
  gtd: { fr: "GTD", en: "GTD" },
  interface: { fr: "Interface", en: "Interface" },
  markdown: { fr: "Markdown", en: "Markdown" },
  methodes: { fr: "Méthodes", en: "Methods" },
  "microsoft-to-do": { fr: "Microsoft To Do", en: "Microsoft To Do" },
  migration: { fr: "Migration", en: "Migration" },
  mobile: { fr: "Mobile", en: "Mobile" },
  notes: { fr: "Notes", en: "Notes" },
  "notes-vocales": { fr: "Notes vocales", en: "Voice notes" },
  notion: { fr: "Notion", en: "Notion" },
  organisation: { fr: "Organisation", en: "Organization" },
  planning: { fr: "Planning", en: "Planning" },
  pomodoro: { fr: "Pomodoro", en: "Pomodoro" },
  "prise-en-main": { fr: "Prise en main", en: "Getting started" },
  priorisation: { fr: "Priorisation", en: "Prioritization" },
  procrastination: { fr: "Procrastination", en: "Procrastination" },
  projets: { fr: "Projets", en: "Projects" },
  recurrence: { fr: "Récurrence", en: "Recurring tasks" },
  revisions: { fr: "Révisions", en: "Exam revision" },
  "revue-hebdomadaire": { fr: "Revue hebdomadaire", en: "Weekly review" },
  rgpd: { fr: "RGPD", en: "GDPR" },
  stockage: { fr: "Stockage", en: "Storage" },
  "tableau-blanc": { fr: "Tableau blanc", en: "Whiteboard" },
  taches: { fr: "Tâches", en: "Tasks" },
  tarifs: { fr: "Tarifs", en: "Pricing" },
  tdah: { fr: "TDAH", en: "ADHD" },
  teletravail: { fr: "Télétravail", en: "Remote work" },
  "time-blocking": { fr: "Time blocking", en: "Time blocking" },
  todoist: { fr: "Todoist", en: "Todoist" },
  trello: { fr: "Trello", en: "Trello" },
};

/** Tags written two ways in the content, folded into one filter. */
const TAG_ALIASES: Record<string, string> = {
  etudiant: "etudiants",
  assistant: "assistant-ia",
};

/**
 * The canonical form of a tag, so near-duplicates ("etudiant" / "etudiants") filter together.
 *
 * @param tag - Tag slug as written in the front matter.
 * @returns The slug the filters and labels use.
 */
export function canonicalTag(tag: string): string {
  return TAG_ALIASES[tag] ?? tag;
}

/**
 * Visible name of a topic tag in the page language. An unknown slug falls back to its
 * words with a capital, never to the raw slug.
 *
 * @param tag - Tag slug (canonical or not).
 * @param locale - Page language.
 * @returns The label.
 */
export function topicLabel(tag: string, locale: Locale): string {
  const known = TOPIC_LABELS[canonicalTag(tag)];
  if (known) return known[locale];
  const words = tag.replace(/-/g, " ");
  return words.charAt(0).toUpperCase() + words.slice(1);
}

/**
 * The filter chips of a listing: canonical, without duplicates, format tags first (in
 * their editorial order) then topics in alphabetical order of their label.
 *
 * @param tags - Every tag of the listed articles.
 * @param locale - Page language (the topic order follows the labels).
 * @returns Canonical tag slugs, ordered.
 */
export function orderedFilterTags(tags: string[], locale: Locale): string[] {
  const unique = [...new Set(tags.map(canonicalTag))];
  const formats = FORMAT_TAGS.filter((f) => unique.includes(f));
  const topics = unique
    .filter((t) => !(FORMAT_TAGS as readonly string[]).includes(t))
    .sort((a, b) => topicLabel(a, locale).localeCompare(topicLabel(b, locale), locale));
  return [...formats, ...topics];
}
